import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  FileText, 
  BookOpen, 
  BarChart3, 
  Plus, 
  Trash, 
  RefreshCw, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Wifi, 
  WifiOff,
  Activity,
  Target,
  Shield,
  AlertTriangle,
  MessageCircle,
  MessageSquare,
  Wand2,
  Heart,
  Video,
  Calculator,
  Type,
  Layers,
  Zap,
  Star,
  Bookmark,
  Play,
  Pause,
  Volume2,
  Trophy,
  GraduationCap,
  Building2
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import RealTimeAdminActions from '../../components/admin/RealTimeAdminActions';
import IndexStatusChecker from '../../components/admin/IndexStatusChecker';
import { useNavigate } from 'react-router-dom';
import { formatTimestamp } from '../../utils/timestampUtils';
import { adminManualCleanup, getCleanupStats } from '../../utils/adminTodoCleanup';

const AdminDashboard = () => {
  const { adminData, isConnected, connectionStatus, logAdminAction } = useRealtime();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [activeTab, setActiveTab] = useState('overview');
  const [systemHealth] = useState({
    server: 'online',
    database: 'connected',
    storage: 75,
    performance: 'good',
    uptime: '99.9%'
  });
  const [cleanupStats, setCleanupStats] = useState({
    totalTodos: 0,
    expiredTodos: 0,
    activeTodos: 0
  });
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const navigate = useNavigate();

  // Calculate real statistics from Firebase data
  const stats = {
    totalUsers: adminData?.users?.length || 0,
    totalTests: adminData?.testResults?.length || 0,
    totalCourses: adminData?.courses?.length || 0,
    totalQuestions: adminData?.questions?.length || 0,
    totalDoubts: adminData?.doubts?.length || 0,
    totalHelpRequests: adminData?.helpRequests?.length || 0,
    totalTodos: adminData?.todos?.length || 0,
    totalVideos: adminData?.videos?.length || 0,
    averageScore: adminData?.analytics?.averageScore || 0,
    completionRate: adminData?.analytics?.completionRate || 0,
    activeUsers: adminData?.analytics?.activeUsers || 0
  };

  // Calculate recent activity metrics
  const recentActivity = (adminData?.adminActions?.slice(0, 5) || []).filter(action => 
    action && 
    typeof action === 'object' && 
    action !== null && 
    !Array.isArray(action)
  );

  // Calculate study counseling stats
  const counselingStats = {
    totalRequests: stats.totalHelpRequests,
    pendingRequests: (adminData?.helpRequests || []).filter(req => req?.status === 'pending').length,
    inProgressRequests: (adminData?.helpRequests || []).filter(req => req?.status === 'in_progress').length,
    completedRequests: (adminData?.helpRequests || []).filter(req => req?.status === 'resolved').length,
    popularCharacters: getPopularCharacters(adminData?.helpRequests || []),
    popularHouses: getPopularHouses(adminData?.helpRequests || [])
  };

  // Calculate question type distribution
  const questionTypeStats = {
    multipleChoice: (adminData?.questions || []).filter(q => q?.type === 'multiple_choice').length,
    integerType: (adminData?.questions || []).filter(q => q?.type === 'integer').length,
    floatingPoint: (adminData?.questions || []).filter(q => q?.type === 'floating_point').length,
    subjective: (adminData?.questions || []).filter(q => q?.type === 'subjective').length,
    matrixMatch: (adminData?.questions || []).filter(q => q?.type === 'matrix_match').length,
    assertionReason: (adminData?.questions || []).filter(q => q?.type === 'assertion_reason').length,
    withLatex: (adminData?.questions || []).filter(q => q?.latexContent).length,
    withChemicalEquations: (adminData?.questions || []).filter(q => q?.chemicalEquation).length
  };

  // Helper functions
  function getPopularCharacters(requests) {
    if (!Array.isArray(requests)) return [];
    
    const characterCount = {};
    requests.forEach(req => {
      if (req && req.assignedCharacter) {
        characterCount[req.assignedCharacter] = (characterCount[req.assignedCharacter] || 0) + 1;
      }
    });
    return Object.entries(characterCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([character, count]) => ({ character, count }));
  }

  function getPopularHouses(requests) {
    if (!Array.isArray(requests)) return [];
    
    const houseCount = {};
    requests.forEach(req => {
      if (req && req.assignedHouse) {
        houseCount[req.assignedHouse] = (houseCount[req.assignedHouse] || 0) + 1;
      }
    });
    return Object.entries(houseCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([house, count]) => ({ house, count }));
  }

  useEffect(() => {
    if (isConnected) {
      setLoading(false);
      setLastUpdated(new Date());
    }
  }, [isConnected, adminData]);

  // Load cleanup stats
  useEffect(() => {
    const loadCleanupStats = async () => {
      try {
        const result = await getCleanupStats();
        if (result.success) {
          setCleanupStats(result.stats);
        }
      } catch (error) {
        // Error loading cleanup stats
      }
    };
    
    loadCleanupStats();
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await logAdminAction('dashboard_refresh', { timestamp: new Date().toISOString() });
      setLastUpdated(new Date());
    } catch (error) {
      setError('Failed to refresh dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleManualCleanup = async () => {
    setCleanupLoading(true);
    try {
      const result = await adminManualCleanup();
      if (result.success) {
        // Reload cleanup stats
        const statsResult = await getCleanupStats();
        if (statsResult.success) {
          setCleanupStats(statsResult.stats);
        }
      } else {
      }
    } catch (error) {
    } finally {
      setCleanupLoading(false);
    }
  };

  const quickActions = [
    {
      title: 'Manage Exams',
      description: 'Add or remove exams',
      icon: Shield,
      color: 'bg-indigo-500',
      hoverColor: 'hover:bg-indigo-600',
      action: () => {
        logAdminAction('quick_action', { action: 'manage_exams' });
        navigate('/admin/exam-management');
      }
    },
    {
      title: 'Add Test',
      description: 'Create new mock test',
      icon: Plus,
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      action: () => {
        logAdminAction('quick_action', { action: 'add_test' });
        navigate('/admin/test-management');
      }
    },
    {
      title: 'Manage Users',
      description: 'View user accounts',
      icon: Users,
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      action: () => {
        logAdminAction('quick_action', { action: 'manage_users' });
        navigate('/admin/user-management');
      }
    },
    {
      title: 'Analytics',
      description: 'View reports',
      icon: BarChart3,
      color: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600',
      action: () => {
        logAdminAction('quick_action', { action: 'view_analytics' });
        navigate('/admin/analytics');
      }
    },
    {
      title: 'Help Requests',
      description: 'MahStry counselling',
      icon: Wand2,
      color: 'bg-pink-500',
      hoverColor: 'hover:bg-pink-600',
      action: () => {
        logAdminAction('quick_action', { action: 'manage_help_requests' });
        navigate('/admin/help-requests');
      }
    },
    {
      title: 'Tuition Teachers',
      description: 'Approve teacher applications',
      icon: GraduationCap,
      color: 'bg-teal-500',
      hoverColor: 'hover:bg-teal-600',
      action: () => {
        logAdminAction('quick_action', { action: 'manage_tuition_teachers' });
        navigate('/admin/tuition-teachers');
      }
    },
    {
      title: 'Teacher Requests',
      description: 'Manage test & video requests',
      icon: MessageSquare,
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      action: () => {
        logAdminAction('quick_action', { action: 'manage_teacher_requests' });
        navigate('/admin/teacher-requests');
      }
    },
    {
      title: 'Coaching Management',
      description: 'Manage coaching accounts',
      icon: Building2,
      color: 'bg-indigo-500',
      hoverColor: 'hover:bg-indigo-600',
      action: () => {
        logAdminAction('quick_action', { action: 'manage_coachings' });
        navigate('/admin/coaching-management');
      }
    },
    {
      title: 'Leaderboards',
      description: 'Manage event leaderboards',
      icon: Trophy,
      color: 'bg-yellow-500',
      hoverColor: 'hover:bg-yellow-600',
      action: () => {
        logAdminAction('quick_action', { action: 'manage_leaderboards' });
        navigate('/admin/leaderboard-management');
      }
    },
    {
      title: 'Video Content',
      description: 'Manage lectures',
      icon: Video,
      color: 'bg-red-500',
      hoverColor: 'hover:bg-red-600',
      action: () => {
        logAdminAction('quick_action', { action: 'manage_videos' });
        navigate('/admin/video-management');
      }
    },
    {
      title: 'Question Bank',
      description: 'Enhanced question types',
      icon: Type,
      color: 'bg-teal-500',
      hoverColor: 'hover:bg-teal-600',
      action: () => {
        logAdminAction('quick_action', { action: 'manage_questions' });
        navigate('/admin/question-bank');
      }
    }
  ];

  const getSystemStatusColor = (status) => {
    switch (status) {
      case 'online':
      case 'connected':
      case 'good':
        return 'admin-badge-success';
      case 'warning':
      case 'moderate':
        return 'admin-badge-warning';
      case 'offline':
      case 'error':
      case 'poor':
        return 'admin-badge-danger';
      default:
        return 'admin-badge-info';
    }
  };

  if (loading) {
    return (
      <div className="admin-content flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading admin dashboard...</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">Status: {connectionStatus}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-content flex items-center justify-center min-h-screen">
        <div className="admin-card p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="admin-section-title mb-2">Dashboard Error</h2>
            <p className="admin-section-subtitle mb-4">{error}</p>
            <button 
              onClick={handleRefresh}
              className="admin-btn-primary"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="admin-section-header">
        <div>
          <h1 className="admin-section-title">
            <Shield className="inline mr-3 text-blue-600" size={28} />
            Admin Dashboard
          </h1>
          <p className="admin-section-subtitle">
            Comprehensive overview of system performance and user activity
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={handleRefresh}
            className="admin-btn-secondary"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <div className="admin-status-indicator admin-status-online">
            <Wifi className="h-3 w-3 mr-1" />
            Connected
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="admin-grid-stats">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-stat-card blue"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Users
              </p>
              <p className="text-2xl font-bold admin-gradient-text">
                {(223 + stats.totalUsers).toLocaleString()}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Active accounts
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
              <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="admin-stat-card green"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Tests
              </p>
              <p className="text-2xl font-bold admin-gradient-text-success">
                {stats.totalTests.toLocaleString()}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Completed tests
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
              <FileText className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="admin-stat-card yellow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Avg Rating
              </p>
              <p className="text-2xl font-bold admin-gradient-text-danger">
                {stats.averageScore.toFixed(1)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                User satisfaction
              </p>
            </div>
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl">
              <Star className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="admin-card p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Quick Actions
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
          {quickActions.map((action, index) => (
            <button
              key={action.title}
              onClick={action.action}
              className={`${action.color} ${action.hoverColor} p-4 rounded-xl text-white transition-all duration-200 transform hover:scale-105 hover:shadow-lg`}
            >
              <action.icon className="w-6 h-6 mx-auto mb-2" />
              <p className="text-xs font-medium text-center">{action.title}</p>
            </button>
          ))}
        </div>
      </motion.div>

      {/* System Health & Recent Activity */}
      <div className="admin-grid-2">
        {/* System Health */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="admin-card p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            System Health
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Server Status</span>
              <span className={`admin-badge ${getSystemStatusColor(systemHealth.server)}`}>
                {systemHealth.server}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Database</span>
              <span className={`admin-badge ${getSystemStatusColor(systemHealth.database)}`}>
                {systemHealth.database}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Storage Usage</span>
              <div className="flex items-center space-x-2">
                <div className="admin-progress w-20">
                  <div 
                    className="admin-progress-bar admin-progress-warning" 
                    style={{ width: `${systemHealth.storage}%` }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400">{systemHealth.storage}%</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Performance</span>
              <span className={`admin-badge ${getSystemStatusColor(systemHealth.performance)}`}>
                {systemHealth.performance}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Uptime</span>
              <span className="admin-badge admin-badge-success">{systemHealth.uptime}</span>
            </div>
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="admin-card p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Recent Activity
          </h3>
          <div className="space-y-3">
            {recentActivity.length > 0 ? (
              recentActivity.map((action, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {action.action || 'Admin action'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatTimestamp(action.timestamp)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No recent activity</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Additional Components */}
      <RealTimeAdminActions />
      <IndexStatusChecker />
    </div>
  );
};

export default AdminDashboard;

